import { useState, useEffect, useMemo, Fragment } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import { formatDate } from '../../utils/format';
import Avatar from '../shared/Avatar';
import { LoadHeat, LoadHeatLegend, heatCellClass } from '../shared/LoadBar';
import UnstaffedWork from './UnstaffedWork';

// Resource Management: everybody's capacity and work, by week or by month, from the
// one engine every resourcing screen now shares (GET /api/resource-load, built
// on src/lib/capacity.js and src/lib/resourceLoad.js).
//
// Capacity is each person's own calendar: contracted hours on their working
// days, less England and Wales bank holidays, closures for everybody or for
// their team, and approved leave by the half day. Demand is every task they
// are assigned to, as a share of their day (or planned hours spread over it).
// Draft and Issued SOWs are tentative work, weighted by win probability and
// drawn striped beside committed work, never added into it.

/** Format YYYY-MM-DD as "27 Mar" */
function fmt(dateStr) {
  const d = new Date(dateStr + 'T00:00:00Z');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** Format YYYY-MM as "Oct 2026" */
function fmtMonth(ym) {
  const d = new Date(ym + '-01T00:00:00Z');
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Format YYYY-MM as "October 2026" */
function fmtMonthLong(ym) {
  const d = new Date(ym + '-01T00:00:00Z');
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Format a number as £xx,xxx */
function fmtMoney(v) {
  if (v == null || isNaN(v)) return '—';
  return '£' + Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const r1 = (n) => Math.round(n * 10) / 10;
const NO_TEAM = '__none__';

export default function ForecastPage() {
  const { navigate } = useApp();
  const [period, setPeriod] = useState('week');               // week | month
  const [weekCount, setWeekCount] = useState(12);
  const [monthCount, setMonthCount] = useState(6);
  const [show, setShow] = useState('free');                   // what a cell says: free | pct | booked
  const [includeTentative, setIncludeTentative] = useState(true);
  const [data, setData] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(() => new Set());       // team ids expanded
  const [selected, setSelected] = useState(null);           // { personId, key }
  const [view, setView] = useState('capacity');             // capacity | unstaffed
  const [unstaffedCount, setUnstaffedCount] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);            // bumped after an assignment

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      api.get('resource-load', {
        ...(period === 'month' ? { period: 'month', months: monthCount } : { weeks: weekCount }),
        tentative: includeTentative ? 1 : 0,
      }),
      api.get('forecast/pipeline').catch(() => []),
    ]).then(([load, pl]) => {
      setData(load);
      setPipeline(Array.isArray(pl) ? pl : []);
    }).catch(e => setError(e.message || 'Could not load resource data.'))
      .finally(() => setLoading(false));
  }, [period, weekCount, monthCount, includeTentative, reloadKey]);

  // How much is waiting in the unstaffed queue, for the tab's label.
  useEffect(() => {
    api.get('resource-load/unstaffed', { tentative: includeTentative ? 1 : 0 })
      .then(d => setUnstaffedCount(d.tasks.length + d.lines.length + (d.allowances || []).length))
      .catch(() => setUnstaffedCount(null));
  }, [includeTentative, reloadKey]);

  // The columns: weeks, or months when the data came back by month. Each
  // person's (and each unstaffed row's) cells line up with them, keyed by
  // week start or by YYYY-MM.
  const byMonth = data?.period === 'month';
  const weeks = data?.weeks || [];
  const columns = byMonth ? (data?.months || []) : weeks;
  const cellsOf = (row) => (byMonth ? row.months : row.weeks) || [];
  const keyOf = (cell) => (byMonth ? cell.month : cell.week);
  const label = (key) => (byMonth ? fmtMonth(key) : fmt(key));
  const periodName = (key) => (byMonth ? fmtMonthLong(key) : `week of ${fmt(key)}`);
  // A month can be fine on the whole and still have a week in it that is over.
  // Weeks are counted against the month their Monday falls in.
  const overWeeksIn = (p, month) => (p.weeks || []).filter(w => w.week.slice(0, 7) === month && w.over_days > 0).length;

  // Teams with their people (by primary team), totals per week, and the work
  // given to the team with nobody named on it.
  const groups = useMemo(() => {
    if (!data) return [];
    const byTeam = new Map((data.teams || []).map(t => [t.id, { ...t, people: [] }]));
    for (const p of data.people || []) {
      const key = p.team_id && byTeam.has(p.team_id) ? p.team_id : NO_TEAM;
      if (!byTeam.has(key)) byTeam.set(key, { id: NO_TEAM, name: 'No team', color: '#9CA3AF', people: [] });
      byTeam.get(key).people.push(p);
    }
    const unstaffed = new Map((data.unstaffed || []).map(u => [u.team_id, u]));
    return [...byTeam.values()]
      .filter(t => t.people.length || unstaffed.has(t.id))
      .map(t => {
        const totals = columns.map((key, i) => {
          let capacity = 0, committed = 0, tentative = 0;
          for (const p of t.people) {
            const c = cellsOf(p)[i];
            if (!c) continue;
            capacity += c.capacity_days; committed += c.committed_days; tentative += c.tentative_days;
          }
          return { key, capacity: r1(capacity), committed: r1(committed), tentative: r1(tentative) };
        });
        const people = [...t.people].sort((a, b) => b.over_weeks - a.over_weeks || String(a.name).localeCompare(String(b.name)));
        const overPeople = people.filter(p => p.over_weeks > 0).length;
        return { ...t, people, totals, unstaffed: unstaffed.get(t.id) || null, overPeople };
      })
      .sort((a, b) => (a.id === NO_TEAM) - (b.id === NO_TEAM) || String(a.name).localeCompare(String(b.name)));
  }, [data, columns, byMonth]);

  const notes = useMemo(() => (data?.people || []).filter(p => (p.notes || []).length || (p.unplaceable || []).length), [data]);
  const overCount = (data?.people || []).filter(p => p.over_weeks > 0).length;
  const toggle = (id) => setOpen(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOpen = groups.length > 0 && groups.every(g => open.has(g.id));

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">Resource Management</h2>
          <div className="text-[9px] text-gray-500">
            {data ? <>
              {(data.people || []).length} people · {byMonth
                ? <>{columns.length} {columns.length === 1 ? 'month' : 'months'} from {columns[0] ? fmtMonthLong(columns[0]) : '—'}</>
                : <>{weeks.length} weeks from {weeks[0] ? fmt(weeks[0]) : '—'}</>}
              {overCount > 0
                ? <span className="ml-2 text-red-600 font-semibold">{overCount} over capacity in at least one week</span>
                : <span className="ml-2 text-emerald-700 font-semibold">Nobody is over capacity in this window</span>}
            </> : 'Everybody\'s capacity and work, by week or by month'}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1 text-[9px] text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={includeTentative} onChange={e => setIncludeTentative(e.target.checked)} />
            Show bid work
          </label>
          <div className="flex rounded border border-gray-200 overflow-hidden ml-2" role="group" aria-label="Weekly or monthly">
            {[['week', 'Weekly'], ['month', 'Monthly']].map(([k, text]) => (
              <button key={k} type="button" onClick={() => { setPeriod(k); setSelected(null); }} aria-pressed={period === k}
                className={`px-2 py-0.5 text-[9px] font-semibold ${period === k ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                {text}
              </button>
            ))}
          </div>
          <span className="text-[9px] text-gray-500 ml-2">Show:</span>
          {(period === 'month' ? [3, 6, 12] : [4, 8, 12, 26, 52]).map(n => {
            const on = period === 'month' ? monthCount === n : weekCount === n;
            return (
              <button
                key={n}
                onClick={() => { (period === 'month' ? setMonthCount : setWeekCount)(n); setSelected(null); }}
                className={`px-2 py-0.5 text-[9px] font-semibold rounded border ${on ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
              >
                {n}{period === 'month' ? 'm' : 'w'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Capacity, or the queue of work nobody is on yet */}
      <div className="flex items-end border-b border-gray-200 mb-3">
        {[['capacity', 'Capacity'], ['unstaffed', `Unstaffed work${unstaffedCount != null ? ` (${unstaffedCount})` : ''}`]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setView(k)}
            className={`px-3 py-1.5 text-[10px] font-medium border-b-2 -mb-px whitespace-nowrap ${view === k ? 'text-gold border-gold font-semibold' : 'text-gray-400 border-transparent hover:text-gray-600'}`}>
            {label}
          </button>
        ))}
      </div>

      {view === 'unstaffed' && (
        <UnstaffedWork includeTentative={includeTentative} onCount={setUnstaffedCount}
          onChanged={() => setReloadKey(k => k + 1)} />
      )}

      {view === 'capacity' && (<>
      <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
        <LoadHeatLegend showTentative={includeTentative} />
        <div className="flex items-center gap-3">
          <div className="flex rounded border border-gray-200 overflow-hidden" role="group" aria-label="What each cell shows">
            {[['free', 'Free days'], ['pct', '% used'], ['booked', 'Days booked']].map(([k, text]) => (
              <button key={k} type="button" onClick={() => setShow(k)} aria-pressed={show === k}
                className={`px-2 py-0.5 text-[9px] font-semibold ${show === k ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                {text}
              </button>
            ))}
          </div>
          {groups.length > 0 && (
            <button onClick={() => setOpen(allOpen ? new Set() : new Set(groups.map(g => g.id)))}
                    className="text-[9px] text-navy underline">
              {allOpen ? 'Collapse all teams' : 'Show everybody'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="mb-2 text-[9px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>}
      {loading && !data && <div className="text-center py-12 text-gray-400 text-sm">Working out capacity…</div>}

      {data && (
        <div className={`bg-white border border-gray-200 rounded overflow-x-auto ${loading ? 'opacity-60' : ''}`}>
          <table className="w-full border-collapse" style={{ minWidth: `${220 + columns.length * (byMonth ? 80 : 64)}px` }}>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold sticky left-0 bg-gray-50 z-10 min-w-[200px]">Team / person</th>
                {columns.map(key => (
                  <th key={key} className="px-1 py-1.5 text-center text-[8px] font-semibold text-gray-500 border-l border-gray-100 min-w-[60px]">
                    {label(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && (
                <tr><td colSpan={1 + columns.length} className="text-center py-8 text-[10px] text-gray-400">Nobody to show. Add people and teams in People.</td></tr>
              )}
              {groups.map(g => {
                const isOpen = open.has(g.id);
                return (
                  <Fragment key={g.id}>
                    {/* Team total */}
                    <tr className="border-b border-gray-100 hover:bg-gold/5 cursor-pointer" onClick={() => toggle(g.id)}>
                      <td className="px-3 py-1.5 sticky left-0 bg-white z-10">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: g.color || '#6B7280' }} />
                          <span className="text-[10px] font-semibold text-navy">{g.name}</span>
                          <span className="text-[8px] text-gray-400">{g.people.length} {g.people.length === 1 ? 'person' : 'people'}</span>
                          <span className="text-[8px] text-gray-400 ml-auto">{isOpen ? '▲' : '▼'}</span>
                        </div>
                        {g.overPeople > 0 && <div className="text-[7.5px] text-red-600 ml-3.5">{g.overPeople} over capacity</div>}
                      </td>
                      {g.totals.map(t => (
                        <td key={t.key} className={`relative p-0 border-l border-gray-100 align-middle ${heatCellClass(t.committed, t.capacity)}`}
                            title={`Team, ${periodName(t.key)}\nCapacity ${t.capacity}d\nCommitted ${t.committed}d${includeTentative ? `\nBid ${t.tentative}d` : ''}\nFree ${r1(Math.max(0, t.capacity - t.committed))}d`}>
                          <LoadHeat committed={t.committed} tentative={t.tentative} capacity={t.capacity} show={show} showTentative={includeTentative} strong />
                        </td>
                      ))}
                    </tr>

                    {/* Work given to the team with nobody named on it */}
                    {g.unstaffed && (
                      <tr className="border-b border-gray-100 bg-amber-50">
                        <td className="px-3 py-1 sticky left-0 bg-amber-50 z-10">
                          <div className="text-[9px] font-semibold text-amber-800 ml-3.5">Unstaffed team work</div>
                          <div className="text-[7.5px] text-amber-700 ml-3.5">{g.unstaffed.tasks.length} task{g.unstaffed.tasks.length === 1 ? '' : 's'} with this team and nobody named</div>
                        </td>
                        {cellsOf(g.unstaffed).map(w => (
                          <td key={keyOf(w)} className="px-1 py-1 text-center border-l border-amber-100 text-[9px]"
                              title={g.unstaffed.tasks.map(t => `${t.sow_reference || ''} ${t.task_name}${t.tentative ? ' (bid)' : ''}`).join('\n')}>
                            {w.committed_days > 0 && <span className="font-semibold text-amber-800">{r1(w.committed_days)}d</span>}
                            {includeTentative && w.tentative_days > 0 && <span className="text-indigo-700 font-semibold"> +{r1(w.tentative_days)}</span>}
                            {!w.committed_days && !(includeTentative && w.tentative_days) && <span className="text-gray-300">·</span>}
                          </td>
                        ))}
                      </tr>
                    )}

                    {/* People */}
                    {isOpen && g.people.map(p => {
                      const sel = selected?.personId === p.person_id ? cellsOf(p).find(c => keyOf(c) === selected.key) : null;
                      return (
                        <Fragment key={p.person_id}>
                          <tr className="border-b border-gray-100 bg-gray-50/60">
                            <td className="px-3 py-1 sticky left-0 bg-gray-50 z-10">
                              <div className="flex items-center gap-1.5 ml-3.5">
                                <Avatar personId={p.person_id}
                                  className="w-5 h-5 rounded-full text-white text-[7px] font-bold flex items-center justify-center flex-shrink-0"
                                  style={{ background: p.avatar_color || 'var(--navy)' }}>
                                  {(p.name || '').split(' ').map(x => x[0]).join('').slice(0, 2)}
                                </Avatar>
                                <div className="min-w-0">
                                  <div className="text-[9px] font-semibold text-gray-800 truncate">{p.name}</div>
                                  <div className="text-[7.5px] text-gray-400 truncate">
                                    {p.designation ? `${p.designation} · ` : ''}{p.contracted_week_hours}h a week
                                    {(p.notes || []).length > 0 && <span className="text-amber-600" title={p.notes.join('\n')}> · check hours</span>}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {cellsOf(p).map(c => {
                              const key = keyOf(c);
                              const isSel = selected?.personId === p.person_id && selected.key === key;
                              const busy = c.committed_days > 0 || c.tentative_days > 0 || c.leave_days > 0;
                              const overWeeks = byMonth ? overWeeksIn(p, key) : 0;
                              return (
                                <td key={key} className={`relative p-0 border-l border-gray-100 align-middle ${heatCellClass(c.committed_days, c.capacity_days)}`}>
                                  <button
                                    type="button"
                                    disabled={!busy}
                                    onClick={() => setSelected(isSel ? null : { personId: p.person_id, key })}
                                    className={`block w-full min-h-[30px] ${busy ? 'hover:ring-1 hover:ring-inset hover:ring-navy cursor-pointer' : 'cursor-default'} ${isSel ? 'ring-2 ring-inset ring-navy' : ''}`}
                                    title={[
                                      `${p.name}, ${periodName(key)}`,
                                      `Capacity ${c.capacity_days}d of ${c.contracted_days}d contracted`,
                                      c.leave_days ? `${c.leave_days}d leave` : null,
                                      `Committed ${c.committed_days}d`,
                                      `Free ${r1(Math.max(0, c.capacity_days - c.committed_days))}d`,
                                      includeTentative && c.tentative_days ? `Bid ${c.tentative_days}d` : null,
                                      c.over_days ? `Over by ${c.over_days}d` : null,
                                      overWeeks && !c.over_days ? `Over capacity in ${overWeeks} ${overWeeks === 1 ? 'week' : 'weeks'} of this month` : null,
                                    ].filter(Boolean).join('\n')}
                                  >
                                    <LoadHeat committed={c.committed_days} tentative={c.tentative_days} capacity={c.capacity_days}
                                              leave={c.leave_days} show={show} showTentative={includeTentative}>
                                      {c.leave_days > 0 && c.capacity_days > 0 && (
                                        <div className="text-[7px] text-sky-700 leading-none -mt-1 pb-1.5 text-center">{r1(c.leave_days)}d off</div>
                                      )}
                                      {overWeeks > 0 && !c.over_days && (
                                        <div className="text-[7px] text-red-700 font-semibold leading-none -mt-1 pb-1.5 text-center">{overWeeks} {overWeeks === 1 ? 'week' : 'weeks'} over</div>
                                      )}
                                    </LoadHeat>
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                          {/* What fills the week or month you clicked, right under it */}
                          {sel && (
                            <tr className="border-b border-gray-100">
                              <td colSpan={1 + columns.length} className="px-3 py-2 bg-navy/5">
                                <div className="text-[9px] font-bold text-navy mb-1">
                                  {p.name}, {periodName(keyOf(sel))}: {sel.committed_days}d committed
                                  {includeTentative && sel.tentative_days > 0 && <>, {sel.tentative_days}d bid</>} against {sel.capacity_days}d available
                                  {sel.leave_days > 0 && <> ({sel.leave_days}d leave)</>}
                                  {sel.over_days > 0 && <span className="text-red-600"> · over by {sel.over_days}d</span>}
                                </div>
                                {sel.tasks.length === 0 ? (
                                  <div className="text-[8.5px] text-gray-500">No work this {byMonth ? 'month' : 'week'}.</div>
                                ) : (
                                  <div className="space-y-0.5">
                                    {sel.tasks.filter(t => includeTentative || !t.tentative).map(t => (
                                      <div key={t.task_id} className="text-[8.5px] text-gray-700">
                                        <button type="button" onClick={() => navigate('sow-detail', t.sow_id)} className="font-mono text-[7.5px] text-navy hover:underline mr-1">{t.sow_reference || 'SOW'}</button>
                                        {t.task_name}
                                        <span className="text-gray-400">
                                          {t.planned_hours ? ` · ${t.planned_hours}h planned` : t.units !== 1 ? ` · ${Math.round(t.units * 100)}% of their time` : ''}
                                        </span>
                                        {t.tentative && <span className="ml-1 text-[7.5px] font-semibold text-indigo-700 bg-indigo-50 px-1 rounded">bid, {Math.round((t.weight ?? 0) * 100)}% likely</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* People whose records need a look before their numbers can be trusted */}
      {notes.length > 0 && (
        <details className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-[9px] text-amber-800">
          <summary className="cursor-pointer font-semibold">{notes.length} {notes.length === 1 ? 'person has' : 'people have'} records worth checking</summary>
          <ul className="mt-1 space-y-0.5 list-disc ml-4">
            {notes.map(p => (
              <li key={p.person_id}>
                <span className="font-semibold">{p.name}:</span>{' '}
                {[...(p.notes || []), ...(p.unplaceable || []).map(u => `${u.sow_reference || ''} ${u.task_name}: ${u.reason}`)].join(' ')}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="mt-2 text-[8px] text-gray-400 leading-relaxed">
        Capacity is each person's contracted hours on the days they work, less England and Wales bank holidays, company and team closures,
        and approved leave by the half day. Work is each task they are on: their days of work on it, spread over their working days in its dates.
        A day is 7.5 hours. Each cell is shaded by how much of the capacity is committed, and shows free days, the
        share used or days booked, as chosen above the grid. Click a person's week or month to see what fills it. In the monthly view a month
        can be within capacity overall and still have a week in it that is over; those are marked under the bar.
      </div>

      {/* ── Pipeline / Likely SOWs ──────────────────────────────────────────── */}
      {pipeline.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-[11px] font-extrabold text-navy">Pipeline: resource lines on likely SOWs</h3>
            <span className="text-[8px] text-gray-400">{pipeline.length} SOW{pipeline.length !== 1 ? 's' : ''} with win probability set</span>
          </div>
          <div className="text-[8px] text-gray-500 mb-3">
            What Draft and Issued SOWs were sold with, by band. Work already planned on these SOWs shows above as bid work.
          </div>
          <div className="bg-white border border-gray-200 rounded overflow-x-auto">
            <table className="w-full text-[9px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-[8px] uppercase text-gray-400 font-bold">
                  <th className="px-3 py-2 text-left">SOW</th>
                  <th className="px-3 py-2 text-left">Client</th>
                  <th className="px-3 py-2 text-left">Likely Start</th>
                  <th className="px-2 py-2 text-center">Win %</th>
                  <th className="px-3 py-2 text-right">Resource Value</th>
                  <th className="px-3 py-2 text-left">Band Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {pipeline.map(sow => {
                  const prob = sow.win_probability ?? 0;
                  const probColor = prob >= 70 ? 'text-emerald-700 bg-emerald-50' : prob >= 40 ? 'text-amber-700 bg-amber-50' : 'text-gray-600 bg-gray-100';
                  return (
                    <tr key={sow.id} className="border-b border-gray-100 hover:bg-gray-50/60">
                      <td className="px-3 py-2">
                        <div className="font-bold text-navy">{sow.reference}</div>
                        <div className="text-[8px] text-gray-500 truncate max-w-[160px]">{sow.detail}</div>
                        <div className="text-[7px] text-gray-400">{sow.status}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{sow.client_name || '—'}</td>
                      <td className="px-3 py-2 font-semibold text-gray-700">{formatDate(sow.likely_start_date)}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full font-bold text-[8px] ${probColor}`}>
                          {prob}%
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-700">{fmtMoney(sow.total_resource_value)}</td>
                      <td className="px-3 py-2">
                        {sow.resource_lines.length === 0 ? (
                          <span className="text-[8px] text-gray-300 italic">No resource lines</span>
                        ) : (
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                            {sow.resource_lines.map((l, i) => (
                              <span key={i} className="text-[8px] text-gray-600">
                                <span className="font-semibold">{l.band_code || l.band_name}</span>
                                {' '}{l.days}d @ £{Number(l.day_rate).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
